import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '../shared/api/errorMessage'
import { isPhone, isPersonName } from '../shared/validation'
import { useAuth } from './account/AuthContext'
import { useCart } from './cart/CartContext'
import {
  useAccountOrders,
  useAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
  updateProfileName,
} from '../shared/api/account'
import { useShop } from '../shared/api/storefront'
import { formatMoney } from '../shared/money'
import type { CustomerAddress, CustomerSession, Order } from '../shared/types'
import { PerforatedDivider } from './components/brand'

/**
 * The customer account area, split into the two bottom-nav destinations sharing one header:
 * `orders` (Your Orders) and `profile` (name + saved addresses). Both require a session — a guest
 * is bounced to /login.
 */
export default function AccountPage({ view = 'orders' }: { view?: 'orders' | 'profile' }) {
  const { t } = useTranslation()
  const { session, isAuthenticated, initializing, logout, updateSession } = useAuth()
  const { data: shop } = useShop()
  const currency = shop?.currency ?? 'INR'
  const navigate = useNavigate()
  const { add } = useCart()
  const [editingName, setEditingName] = useState(false)

  const orders = useAccountOrders(isAuthenticated && view === 'orders')
  const addresses = useAddresses(isAuthenticated && view === 'profile')

  if (initializing) {
    // The boot silent refresh is restoring the session (D-053) — don't bounce to login mid-flight.
    return (
      <Container maxWidth="md" sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    )
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  const reorder = (order: Order) => {
    // Re-add from the order's snapshot (name + price at order time); the server re-prices at checkout.
    order.items.forEach((i) =>
      add({ id: i.productId, name: i.productName, description: null, price: i.unitPrice, imageUrl: null }, i.quantity))
    navigate('/checkout')
  }

  return (
    <Container maxWidth="md" sx={{ py: 3, pb: 8 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="h5" fontWeight={700}>
              {t('account.greeting', { name: session?.name })}
            </Typography>
            <IconButton size="small" aria-label={t('account.editName')} onClick={() => setEditingName(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {session?.phone}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={logout} sx={{ borderRadius: '10px', borderColor: 'primary.main', color: 'primary.main' }}>
          {t('common.logOut')}
        </Button>
      </Stack>

      {editingName && (
        <NameDialog
          initial={session?.name ?? ''}
          onClose={() => setEditingName(false)}
          onSaved={(s) => {
            updateSession(s)
            setEditingName(false)
          }}
        />
      )}

      {view === 'orders' && (
      <>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {t('account.yourOrders')}
      </Typography>
      {orders.isLoading ? (
        <CircularProgress />
      ) : !orders.data || orders.data.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          {t('account.noOrders')}
        </Typography>
      ) : (
        <Stack spacing={2} sx={{ mb: 4 }}>
          {orders.data.map((order) => (
            // Tapping the card opens the order's live track page; Reorder stops propagation below.
            <Card
              key={order.id}
              variant="outlined"
              onClick={() => navigate(`/track/${order.trackingToken}`)}
              sx={{ cursor: 'pointer', borderColor: 'rgba(59,42,30,0.12)', '&:hover': { borderColor: 'primary.main', boxShadow: '0 3px 16px rgba(59,42,30,0.20)' } }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography fontWeight={700}>{t('common.orderNumber', { number: order.orderNumber })}</Typography>
                  <Chip label={t(`status.${order.status}`, { defaultValue: order.status })} size="small" />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {order.items.map((i) => `${i.productName} × ${i.quantity}`).join(', ')}
                </Typography>
                <PerforatedDivider sx={{ mb: 1.5 }} />
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  {/* Priceless order (D-069): null total → no money, keep reorder right-aligned. */}
                  {order.total != null ? (
                    <Typography fontWeight={700}>{formatMoney(order.total, currency)}</Typography>
                  ) : (
                    <Box />
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ borderRadius: '10px', px: 2 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      reorder(order)
                    }}
                  >
                    {t('account.reorder')}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
      </>
      )}

      {view === 'profile' && (
        <AddressBook addresses={addresses.data ?? []} loading={addresses.isLoading} />
      )}
    </Container>
  )
}

function NameDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: string
  onClose: () => void
  onSaved: (s: CustomerSession) => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState(initial)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const nameError = name.trim() !== '' && !isPersonName(name)
  const valid = name.trim() && !nameError

  const save = async () => {
    if (!valid) {
      return
    }
    setSaving(true)
    setError(null)
    try {
      onSaved(await updateProfileName(name.trim()))
    } catch (err) {
      setError(errorMessage(err, t('account.saveNameError')))
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('account.editName')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('common.yourName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
            error={nameError}
            helperText={nameError ? t('validation.name') : ' '}
            onKeyDown={(e) => e.key === 'Enter' && save()}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={save}>
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function AddressBook({ addresses, loading }: { addresses: CustomerAddress[]; loading: boolean }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<CustomerAddress | null>(null)
  const [adding, setAdding] = useState(false)

  // Parent's useAddresses query listens on this key → invalidating it refetches the list.
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['account', 'addresses'] })

  const remove = async (id: string) => {
    await deleteAddress(id)
    refresh()
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="h6">{t('account.savedAddresses')}</Typography>
        <Button variant="outlined" size="small" onClick={() => setAdding(true)} sx={{ borderRadius: '10px', borderColor: 'primary.main', color: 'primary.main' }}>
          {t('account.addAddress')}
        </Button>
      </Stack>

      {loading ? (
        <CircularProgress />
      ) : addresses.length === 0 ? (
        <Typography color="text.secondary">{t('account.noSavedAddresses')}</Typography>
      ) : (
        <Stack spacing={1.5}>
          {addresses.map((a) => (
            <Card key={a.id} variant="outlined" sx={{ borderColor: 'rgba(59,42,30,0.12)' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography fontWeight={600}>{a.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {a.address}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {a.phone}
                  </Typography>
                </Box>
                <IconButton aria-label={t('common.edit')} onClick={() => setEditing(a)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton aria-label={t('common.delete')} color="error" onClick={() => remove(a.id)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {(adding || editing) && (
        <AddressDialog
          initial={editing}
          onClose={() => {
            setAdding(false)
            setEditing(null)
          }}
          onSaved={() => {
            setAdding(false)
            setEditing(null)
            refresh()
          }}
        />
      )}
    </Box>
  )
}

function AddressDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: CustomerAddress | null
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const [label, setLabel] = useState(initial?.label ?? '')
  const [address, setAddress] = useState(initial?.address ?? '')
  // A new address is almost always the customer's own number — prefill it (still editable, so
  // an address for a parent or a shopfront can carry a different one). Checkout does the same.
  const [phone, setPhone] = useState(initial?.phone ?? session?.phone ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const phoneError = phone.trim() !== '' && !isPhone(phone)
  const valid = label.trim() && address.trim() && phone.trim() && !phoneError

  const save = async () => {
    if (!valid) {
      return
    }
    setSaving(true)
    setError(null)
    const payload = { label: label.trim(), address: address.trim(), phone: phone.trim() }
    try {
      if (initial) {
        await updateAddress(initial.id, payload)
      } else {
        await createAddress(payload)
      }
      onSaved()
    } catch (err) {
      // This used to fail completely silently — the spinner stopped and the dialog just sat
      // there, so the customer had no way to tell a save from a failure.
      setError(errorMessage(err, t('account.saveAddressError')))
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{initial ? t('account.editAddress') : t('account.addAddress')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label={t('account.label')} value={label} onChange={(e) => setLabel(e.target.value)} fullWidth />
          <TextField
            label={t('account.address')}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label={t('common.phone')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            type="tel"
            fullWidth
            error={phoneError}
            helperText={phoneError ? t('validation.phone') : ' '}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={save}>
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
